import { In } from 'typeorm'

import { Domain, getDataSource } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import { runScenario } from '@things-factory/integration-base'
import { ScheduleRegisterRequest } from '@things-factory/scheduler-client'

import { createDataSample } from './controllers/create-data-sample.js'
import { renderJasperReport } from './controllers/jasper-report.js'
import { renderShinyReport } from './controllers/shiny-report.js'
import { issueDataCollectionTask } from './controllers/issue-data-collection-task.js'
import { DataSensor } from './service/data-sensor/data-sensor.js'
import { DataSet } from './service/data-set/data-set.js'

process.on('bootstrap-module-global-public-route' as any, (app, globalPublicRouter) => {
  /*
   * can add global public routes to application (auth not required, tenancy not required)
   *
   * ex) routes.get('/path', async(context, next) => {})
   * ex) routes.post('/path', async(context, next) => {})
   */

  globalPublicRouter.post('/sensor-data', async (context, next) => {
    // 데이타 검증
    const { deviceId, data, rawData, timestamp = Date.now() } = context.request.body
    if (!deviceId) {
      throw new Error(`The deviceId(${deviceId}) property are mandatory`)
    }

    // make new data-sample
    await getDataSource().transaction(async tx => {
      // find sensor through deviceId
      const sensor = await tx.getRepository(DataSensor).findOne({
        where: { deviceId },
        relations: ['domain', 'appliance', 'dataSet', 'decoder']
      })

      if (!sensor) {
        throw new Error(`Sensor having given deviceId(${deviceId}) not found`)
      }

      if (!sensor.active) {
        throw new Error(`State of the sensor given deviceId(${deviceId}) is not active`)
      }

      if (!sensor.appliance) {
        throw new Error(`Appliance of the sensor given deviceId(${deviceId}) is not set up`)
      }

      const domain = sensor.domain
      const dataSet = sensor.dataSet

      /* 
        원칙적으로 user 인증정보를 가지고 요청해야 하지만, 인증정보를 보낼 수 없는 상황이라고 전제하여 처리한다.
        앞으로, 보완되어야 한다.
      */
      const user: User =
        context.user ||
        (await tx.getRepository(User).findOne({
          where: {
            reference: sensor.appliance.id,
            userType: 'appliance'
          }
        }))

      context.state = {
        user,
        ...context.state,
        domain,
        tx
      }

      var decoded
      if (sensor.decoder) {
        const { name: scenarioName } = sensor.decoder
        const variables = {
          dataSensor: sensor,
          source: deviceId,
          data,
          rawData,
          timestamp
        }

        decoded = (await runScenario(null, scenarioName, variables, context))?.result
      }

      if (dataSet) {
        await createDataSample(
          {
            dataSet,
            data,
            rawData,
            source: deviceId,
            collectedAt: new Date(timestamp),
            ...decoded
          },
          context
        )
      }

      context.status = 200
      context.body = {
        data,
        ...decoded
      }
    })
  })

  /* When a callback occurs from the scheduler when a scheduled dataset is on schedule, data collection task for the dataset should be issued. */
  globalPublicRouter.post('/callback-schedule-for-dataset', async (context, next) => {
    const { client } = context.request.body as ScheduleRegisterRequest

    if (!client || typeof client !== 'object') {
      throw new Error('client property should be a part of callback body.')
    }

    const { group: domainId, key: dataSetId } = client

    if (!domainId || !dataSetId) {
      throw new Error(`group(${domainId}) and key(${dataSetId}) properties should not be empty`)
    }

    await issueDataCollectionTask(domainId, dataSetId, context)

    context.status = 200
  })

  /* When a callback occurs from the scheduler when a scheduled summary is on schedule, data summary procedure for the dataset should be executed. */
  globalPublicRouter.post('/callback-schedule-for-data-summary', async (context, next) => {
    const { client } = context.request.body as ScheduleRegisterRequest

    if (!client || typeof client !== 'object') {
      throw new Error('client property should be a part of callback body.')
    }

    const { group: domainId, key: dataSetId } = client

    if (!domainId || !dataSetId) {
      throw new Error(`group(${domainId}) and key(${dataSetId}) properties should not be empty`)
    }

    await getDataSource().transaction(async tx => {
      const domain = await tx.getRepository(Domain).findOneBy({ id: domainId })

      if (!domain) {
        throw new Error(`domain(${domainId}) not found`)
      }

      const dataSet = await tx
        .getRepository(DataSet)
        .findOne({ where: { domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, id: dataSetId } })

      // do what you gotta do
    })

    context.status = 200
  })
})

process.on('bootstrap-module-global-private-route' as any, (app, globalPrivateRouter) => {
  /*
   * can add global private routes to application (auth required, tenancy not required)
   */
})

process.on('bootstrap-module-domain-public-route' as any, (app, domainPublicRouter) => {
  /*
   * can add domain public routes to application (auth not required, tenancy required)
   */
})

process.on('bootstrap-module-domain-private-route' as any, (app, domainPrivateRouter) => {
  /*
   * can add domain private routes to application (auth required, tenancy required)
   */
  domainPrivateRouter.get('/data-report/jasper', async (context, next) => {
    context.body = await renderJasperReport(context)
  })

  domainPrivateRouter.get('/data-report/shiny', async (context, next) => {
    await renderShinyReport(context)
  })

  domainPrivateRouter.get('/data-report/:reportType', async (context, next) => {
    let { reportType } = context.params
    context.body = `${reportType} Not Implemented.`
  })
})
